import type { ReactNode } from "react";
import { Icon, type IconName } from "@/components/ui/icon";
import { Skeleton } from "@/components/ui/states";
import { compactNumber } from "@/lib/format";
import { cn } from "@/lib/cn";
import { EMPHASIS, SEQUENTIAL, STATUS } from "./tokens";

interface StatTileProps {
  label: string;
  value: number | string | null | undefined;
  /** Small qualifier under the value: "of 24 working days", "this month". */
  meta?: string;
  icon?: IconName;
  loading?: boolean;
  /** 12-point sparkline. Only pass real history — never a synthesised curve. */
  trend?: number[];
  /** Signed change against a named period. */
  delta?: { value: number; period: string; goodDirection?: "up" | "down" };
  className?: string;
}

/**
 * A single current value is a stat tile, not a one-bar bar chart. The value uses
 * the font's proportional figures (tabular-nums makes a large standalone number
 * look loose) and the same sans as everything else — never a display face.
 */
export function StatTile({
  label,
  value,
  meta,
  icon,
  loading,
  trend,
  delta,
  className,
}: StatTileProps) {
  const display =
    value === null || value === undefined
      ? "—"
      : typeof value === "number"
        ? compactNumber(value)
        : value;

  return (
    <div className={cn("flex flex-col gap-2 rounded-xl border border-line bg-surface p-4", className)}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-[13px] leading-tight text-muted">{label}</p>
        {icon && <Icon name={icon} size={16} className="mt-0.5 shrink-0 text-faint" />}
      </div>

      {loading ? (
        <Skeleton className="h-8 w-20" />
      ) : (
        <p className="text-3xl font-semibold leading-none tracking-tight text-ink">{display}</p>
      )}

      {(meta || delta) && !loading && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {delta && <DeltaChip {...delta} />}
          {meta && <span className="text-xs leading-normal text-muted">{meta}</span>}
        </div>
      )}

      {trend && trend.length > 1 && !loading && <Sparkline points={trend} />}
    </div>
  );
}

function DeltaChip({
  value,
  period,
  goodDirection = "up",
}: {
  value: number;
  period: string;
  goodDirection?: "up" | "down";
}) {
  if (value === 0) {
    return (
      <span className="flex items-center gap-1 text-xs text-muted">
        <Icon name="minus" size={16} />
        No change {period}
      </span>
    );
  }
  const rising = value > 0;
  const isGood = goodDirection === "up" ? rising : !rising;
  return (
    <span
      className={cn("flex items-center gap-1 text-xs font-medium", isGood ? "text-good" : "text-bad")}
    >
      <Icon name={rising ? "arrow-up" : "arrow-down"} size={16} />
      <span className="tabular-nums">
        {rising ? "+" : ""}
        {value}
      </span>
      <span className="font-normal text-muted">{period}</span>
    </span>
  );
}

/** 12-point sparkline: recessive line, current point in the accent. */
function Sparkline({ points }: { points: number[] }) {
  const width = 120;
  const height = 28;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const span = max - min || 1;
  const step = width / Math.max(1, points.length - 1);

  const path = points
    .map((p, i) => {
      const x = i * step;
      const y = height - ((p - min) / span) * (height - 4) - 2;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  const lastValue = points[points.length - 1] ?? 0;
  const lastX = (points.length - 1) * step;
  const lastY = height - ((lastValue - min) / span) * (height - 4) - 2;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="mt-0.5 h-7 w-full"
      preserveAspectRatio="none"
      aria-hidden
    >
      <path d={path} fill="none" stroke={EMPHASIS.rest} strokeWidth={2} strokeLinecap="round" />
      <circle cx={lastX} cy={lastY} r={3} fill={EMPHASIS.focus} />
    </svg>
  );
}

interface MeterProps {
  label: string;
  used: number;
  total: number;
  unit?: string;
  /** Above this share of the total, the fill escalates to warn then bad. */
  escalate?: boolean;
  className?: string;
}

/**
 * A single ratio against a limit is a meter, never a two-slice pie. The unfilled
 * track is a lighter step of the fill's own ramp so state reads across the whole
 * bar rather than only in the filled part.
 */
export function Meter({ label, used, total, unit = "days", escalate = true, className }: MeterProps) {
  const safeTotal = total > 0 ? total : 0;
  const ratio = safeTotal > 0 ? Math.min(1, used / safeTotal) : 0;
  const fill = !escalate
    ? SEQUENTIAL[3]
    : ratio >= 0.9
      ? STATUS.bad
      : ratio >= 0.7
        ? STATUS.warn
        : SEQUENTIAL[3];

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[13px] text-ink-soft">{label}</span>
        <span className="font-mono text-xs tabular-nums text-muted">
          {formatMeterValue(used)} / {safeTotal > 0 ? formatMeterValue(safeTotal) : "—"} {unit}
        </span>
      </div>
      <div
        className="h-2 w-full overflow-hidden rounded-full"
        style={{ backgroundColor: SEQUENTIAL[0] }}
        role="meter"
        aria-valuenow={used}
        aria-valuemin={0}
        aria-valuemax={safeTotal || undefined}
        aria-label={`${label}: ${formatMeterValue(used)} of ${safeTotal} ${unit} used`}
      >
        <div
          className="h-full rounded-full transition-[width] duration-300"
          style={{ width: `${ratio * 100}%`, backgroundColor: fill }}
        />
      </div>
    </div>
  );
}

function formatMeterValue(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

/** The one number a page leads with. Exactly one per view. */
export function HeroFigure({
  value,
  label,
  meta,
  loading,
}: {
  value: number | string;
  label: string;
  meta?: ReactNode;
  loading?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <p className="eyebrow">{label}</p>
      {loading ? (
        <Skeleton className="h-12 w-32" />
      ) : (
        <p className="text-5xl font-semibold leading-none tracking-tight text-ink">
          {typeof value === "number" ? compactNumber(value) : value}
        </p>
      )}
      {meta && <div className="mt-1 text-sm leading-relaxed text-muted">{meta}</div>}
    </div>
  );
}
